(() => {
  const tagName = 'rd-proof-rotator';
  if (customElements.get(tagName)) return;

  class RawDawgProofRotator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.slides = [...this.querySelectorAll('[data-rd-proof-rotator-slide]')];
      if (this.slides.length === 0) return;

      this.ready = true;
      this.toggle = this.querySelector('[data-rd-proof-rotator-pause]');
      this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.minInterval = Math.max(10, Number(this.dataset.minInterval) || 10) * 1000;
      this.maxInterval = Math.max(this.minInterval, Math.min(20, Number(this.dataset.maxInterval) || 20) * 1000);
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      if (this.autoplay && !this.mediaQuery.matches && !window.Shopify?.designMode) {
        this.currentIndex = Math.floor(Math.random() * this.slides.length);
      }
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.deck = [];
      this.timer = null;

      this.onToggle = () => {
        this.userPaused = !this.userPaused;
        this.updateToggle();
        this.syncTimer();
      };
      this.onMouseEnter = () => {
        this.hoverPaused = true;
        this.syncTimer();
      };
      this.onMouseLeave = () => {
        this.hoverPaused = false;
        this.syncTimer();
      };
      this.onFocusIn = () => {
        this.focusPaused = true;
        this.syncTimer();
      };
      this.onFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.syncTimer();
      };
      this.onVisibilityChange = () => this.syncTimer();
      this.onMotionChange = () => this.syncTimer();
      this.onBlockSelect = (event) => {
        const slide = event.target?.closest?.('[data-rd-proof-rotator-slide]');
        if (!slide || !this.contains(slide)) return;
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.show(index);
        this.userPaused = true;
        this.updateToggle();
        this.syncTimer();
      };

      this.toggle?.addEventListener('click', this.onToggle);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.mediaQuery.addEventListener?.('change', this.onMotionChange);

      this.show(this.currentIndex);
      this.updateToggle();
      this.syncTimer();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (!this.ready) return;
      this.clearTimer();
      this.toggle?.removeEventListener('click', this.onToggle);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.mediaQuery?.removeEventListener?.('change', this.onMotionChange);
      this.ready = false;
    }

    shuffle(values) {
      const shuffled = [...values];
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const target = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
      }
      return shuffled;
    }

    refillDeck() {
      this.deck = this.shuffle(this.slides.map((_, index) => index).filter((index) => index !== this.currentIndex));
    }

    next() {
      if (this.deck.length === 0) this.refillDeck();
      const index = this.deck.shift();
      if (Number.isInteger(index)) this.show(index);
      this.syncTimer();
    }

    show(index) {
      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    randomDelay() {
      return this.minInterval + Math.random() * (this.maxInterval - this.minInterval);
    }

    clearTimer() {
      if (this.timer === null) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    shouldRun() {
      return this.autoplay &&
        !this.userPaused &&
        !this.hoverPaused &&
        !this.focusPaused &&
        !document.hidden &&
        !this.mediaQuery.matches &&
        !window.Shopify?.designMode;
    }

    syncTimer() {
      this.clearTimer();
      if (this.shouldRun()) this.timer = window.setTimeout(() => this.next(), this.randomDelay());
    }

    updateToggle() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.setAttribute('aria-label', this.userPaused ? 'Play customer stories' : 'Pause customer stories');
    }
  }

  customElements.define(tagName, RawDawgProofRotator);
})();
